<script setup lang="ts">
import { formatRatioPercent, negateMoney, type Ratio } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { CashFlowReport } from '../internal/types.ts'

const props = defineProps<{ cashFlow: CashFlowReport }>()
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

function percentText(value: Ratio): string {
  return `${formatRatioPercent(value, { locale: localeCode.value })}%`
}

const savingsRate = computed(() => props.cashFlow.savingsRate)
const previousSavingsRate = computed(() => props.cashFlow.previousPeriod.savingsRate)
</script>

<template>
  <div class="flex flex-col gap-[var(--space-group)]">
    <div class="flex flex-col gap-1">
      <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('dashboard.savingsRate') }}
      </p>
      <p class="text-[length:var(--text-step-5)] font-semibold tabular-nums text-[color:var(--color-text)]">
        {{ savingsRate === undefined ? t('dashboard.noSavingsRate') : percentText(savingsRate) }}
      </p>
      <p
        v-if="previousSavingsRate !== undefined"
        class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
      >
        {{ t('dashboard.savingsRateBefore', { rate: percentText(previousSavingsRate) }) }}
      </p>
    </div>
    <dl class="grid grid-cols-2 gap-[var(--space-group)]">
      <div class="flex min-w-0 flex-col gap-1">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('dashboard.saved') }}
        </dt>
        <dd class="whitespace-nowrap font-semibold tabular-nums text-[color:var(--color-text)]">
          <MoneyAmount :value="cashFlow.net" sign size="medium" />
        </dd>
      </div>
      <div class="flex min-w-0 flex-col gap-1">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('dashboard.spent') }}
        </dt>
        <dd class="whitespace-nowrap font-semibold tabular-nums text-[color:var(--color-expense)]">
          <MoneyAmount :value="negateMoney(cashFlow.expense)" sign size="medium" />
        </dd>
      </div>
    </dl>
  </div>
</template>
